import { useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface ColunaSaida { titulo: string; numerica: boolean }

/** Linha pronta do servidor: `grupo` traz só o título do grupo; `subtotal` e `total` trazem as somas nas colunas somadas. */
export interface LinhaSaida { tipo: 'linha' | 'grupo' | 'subtotal' | 'total'; celulas: string[] }

/** Tabela pronta que o servidor devolve em `/exportar?formato=tabela`: textos já como a pessoa lê na tela. */
export interface TabelaImpressao {
  titulo: string;
  colunas: ColunaSaida[];
  linhas: string[][];
  truncado: boolean;
}

/** A mesma tabela na tela do relatório e no papel: o servidor manda o texto pronto, aqui só se desenha. */
export function TabelaSaida({ colunas, linhas }: { colunas: ColunaSaida[]; linhas: LinhaSaida[] }) {
  return (
    <table className="tabela-saida">
      <thead>
        <tr>{colunas.map((c) => <th key={c.titulo} data-numerica={c.numerica || undefined}>{c.titulo}</th>)}</tr>
      </thead>
      <tbody>
        {linhas.map((l, i) => (l.tipo === 'grupo'
          ? <tr key={i} data-tipo="grupo"><th colSpan={colunas.length} scope="rowgroup">{l.celulas[0]}</th></tr>
          : <tr key={i} data-tipo={l.tipo}>{l.celulas.map((v, j) => <td key={j} data-numerica={colunas[j]?.numerica || undefined}>{v}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}

interface Props {
  titulo: string;
  colunas: ColunaSaida[];
  linhas: LinhaSaida[];
  registros: number;
  truncado: boolean;
  /** Quem imprime: empresa e filial correntes. */
  origem: string;
  /** Recorte aplicado (busca ou respostas do relatório), para o papel dizer o que ficou de fora. */
  filtro?: string;
  aoTerminar: () => void;
}

/**
 * Folha de impressão: a tela usa grade virtual (só desenha o que cabe), então a impressão monta a tabela inteira fora do app e chama o diálogo
 * do navegador — que também salva em PDF. Some assim que o diálogo fecha.
 */
export function FolhaImpressao({ titulo, colunas, linhas, registros, truncado, origem, filtro, aoTerminar }: Props) {
  useEffect(() => {
    window.addEventListener('afterprint', aoTerminar, { once: true });
    window.print();
    return () => window.removeEventListener('afterprint', aoTerminar);
  }, [aoTerminar]);

  const agora = new Date().toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  return createPortal(
    <div className="folha-impressao" data-testid="folha-impressao">
      <header>
        <strong>{titulo}</strong>
        <span>{origem}</span>
      </header>
      <p>
        {registros} {registros === 1 ? 'registro' : 'registros'}
        {filtro ? ` · filtro: ${filtro}` : ''} · impresso em {agora}
        {truncado ? ' · lista cortada: exporte a planilha para ver tudo' : ''}
      </p>
      <TabelaSaida colunas={colunas} linhas={linhas} />
    </div>,
    document.body,
  );
}
